import React, { useState } from 'react';
import ListViewContainer from '../shared/ListViewContainer';
import Button from 'components/elements/button';
import Box from 'components/elements/Box';
import { Input, Meta } from '../shared/styles';
import { ICustomListData } from '../AddToList/shared';
import ImagePicker, {
  IImagePickerItem,
} from 'components/customlists/shared/ImagePicker/ImagePicker';
import { logToAmplitude } from 'helpers/logAmplitudeEvent';

interface IEditListProps {
  list: ICustomListData;
  close: () => void;
  cancel?: () => void;
  saveList: (list: ICustomListData) => any;
  saveButtonLabel: string;
  title: string;
  pending?: boolean;
  error?: string;
}

const IMAGE_PICKER_ITEMS = [
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_5.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_2.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_3.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_4.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_1.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_list_6.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_5.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_2.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_3.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_4.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_1.png',
  },
  {
    url: 'https://cdnwmgdsp-a.akamaihd.net/dv/images/list/cl_wmg_6.png',
  },
];

/**
 * Component that allows the display name of a customlist to be edited.
 * The save and cancel methods are injected, so this component can be used
 * to either create a new list or edit an existing one.
 */
const EditList: React.FC<IEditListProps> = (props) => {
  const {
    list,
    close,
    cancel,
    saveList,
    saveButtonLabel,
    title,
    pending,
    error,
  } = props;
  const [displayName, setDisplayName] = useState<string>(list.displayName);
  const [customList] = useState<ICustomListData>(list);

  const listNameMinLength = 1;

  const cancelAction = cancel ?? close;

  const isNameValid = displayName.length >= listNameMinLength;

  const save = (): void => {
    if (customList && displayName.length) {
      saveList({
        ...customList,
        displayName,
        thumbnailUrl: selectedImagePickerItem.url,
      });
      logToAmplitude('User has created a new list');
    }
  };

  const [selectedImagePickerItem, setSelectedImagePickerItem] =
    useState<IImagePickerItem>({
      url: list.thumbnailUrl,
    });

  return (
    <ListViewContainer
      title={title}
      close={close}
      rejectedMessage={
        error ? 'An error occurred, please try again.' : undefined
      }
    >
      <Box>
        <Input
          type="text"
          value={displayName}
          pattern={`.{${listNameMinLength},}`}
          required
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            setDisplayName(e.target.value)
          }
          placeholder="Enter a name for your new list"
          mb={4}
          data-pw="custom-lists-edit-list-display-name"
        />
        <ImagePicker
          items={IMAGE_PICKER_ITEMS}
          fallbackItemIndex={0}
          selectedItem={selectedImagePickerItem}
          onChange={({ item }): any => setSelectedImagePickerItem(item)}
        />
        <Meta>
          Please provide a clear, descriptive and unique name for your list when
          it’s used for sharing purposes. You can always change the name later
          if this is needed.
        </Meta>
      </Box>

      <Box
        mt={'2em'}
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-end"
        mb={'2em'}
        data-addtolist
      >
        <Button
          onClick={cancelAction}
          variant={pending ? 'disabled' : 'secondary'}
          alignSelf="auto"
          disabled={pending}
          borderRadius="3px"
        >
          Cancel
        </Button>
        <Button
          variant={!isNameValid || pending ? 'disabled' : 'primary'}
          alignSelf="auto"
          marginLeft=".5em"
          onClick={save}
          disabled={!isNameValid || pending}
          data-gtag="custom-list-new-list"
          data-pw="custom-lists-save-new-list-btn"
          borderRadius="3px"
        >
          {saveButtonLabel}
        </Button>
      </Box>
    </ListViewContainer>
  );
};

export default EditList;
